<template>
  <v-app>

    <!-- Add the top bar and the navigation side menu here -->
    <AccesslinkTopBar @toggleSidemenu="toggleSidemenu()" :pod="pod" :parentPod="parentPod" />
    <QuickPodSwitcher
      v-if="quickSwitcherPods.length > 1"
      :viewPodId="pod.id"
      :pods="quickSwitcherPods"
      :visible="isQuickSwitcherVisible"
      @requestHide="maybeHideQuickSwitcher"
      @switchToPod="switchToPod"
    />
    <AccesslinkSidemenu
      v-model="sidemenuDrawer"
      :pod="pod"
      :parentPod="parentPod"
      @commentTimeFocus="onCommentTimeFocus"
    />

    <v-main>
      <v-container fluid id="accesslink-main-container" style="">
        <v-row>
          <v-col>

            <div v-if="isVideoPod" >
              <PlyrPlayer
                v-if="hasTranscodedFile"
                viewContext="accesslink"
                :pod="pod"
                :access-expires-at="getAccesslinkInfo?.expireat || null"
                :comments="getCommentsWithStart"
                :allowSourceSelection="allowSourceSelection"
                allow-quality-selection
                :persist-settings="false"
              />
              <div v-else>
                <NoPreviewAvailable assetType="video"></NoPreviewAvailable>
              </div>
            </div>

            <div v-if="isAudioPod">
              <AudioPlayer
                v-if="hasTranscodedFile"
                :pod="pod"
                :comments="getCommentsWithStart"
                :persist-settings="false"
              />
              <div v-else>
                <NoPreviewAvailable assetType="audio"></NoPreviewAvailable>
              </div>
            </div>

            <div v-if="isImagePod">
              <PodImageView v-if="hasThumbnailFile" :pod="pod" />
              <div v-else>
                <NoPreviewAvailable assetType="image"></NoPreviewAvailable>
              </div>
            </div>

            <v-row v-if="isArchivePod">
              <v-col v-if="isRefreshingArchiveContents" class="text-center" role="status" aria-live="polite">
                <v-progress-circular
                  indeterminate
                  size="70"
                  width="7"
                  color="primary"
                  :aria-label="$t('routes.pod.loadingArchiveContents')"
                />
                <div>{{ $t('routes.pod.loadingArchiveContents') }}</div>
              </v-col>
              <v-col v-else-if="listArchiveContentsError" class="text-center" role="alert">
                <div class="bg-error pa-3 rounded d-flex align-center ga-2">
                  <v-icon icon="mdi-lock" />
                  {{ listArchiveContentsError }}
                </div>
              </v-col>
              <v-col v-else-if="archiveContents !== null" offset="1" cols="9">
                <v-treeview
                  v-model:opened="openedTreeNode"
                  :items="archiveContents"
                  density="compact"
                  item-title="name"
                  item-value="id"
                  activatable
                  open-on-click
                  :aria-label="pod.name"
                >
                  <template v-slot:prepend="{ item, isOpen }">
                    <v-icon v-if="item.type === 'd'" :icon="isOpen ? 'mdi-folder-open' : 'mdi-folder'"/>
                    <v-icon v-else icon="mdi-file"/>
                  </template>

                  <template v-slot:append="{ item }">
                    <div v-if="item.type === 'f'">{{ formatSize(item.size) }}</div>
                  </template>
                </v-treeview>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row v-if="getNotes && getAccesslinkPods && getAccesslinkPods.length === 1" data-cy="accessLink-view-notes">
          <v-col>
            <div v-html="getNotes" class="accesslink-notes markdown-preview" style="" role="note" />
          </v-col>
        </v-row>

        <v-row v-if="downloadingAllowed" class="">
          <v-col cols="4" offset="4">
            <v-btn color="primary" variant="flat" v-on:click="downloadAsset" :elevation="0" block data-cy="accessLink-view-download-btn" :aria-label="$t('routes.accesslink.download')">
              {{ $t('routes.accesslink.download') }}
            </v-btn>
          </v-col>
        </v-row>

      </v-container>
    </v-main>

  </v-app>
</template>

<script>
import { marked } from 'marked';
import { useTheme } from 'vuetify';
import { usePlayerStore } from '@/stores/PlayerStore.js'
import { useAccesslinkStore } from '@/stores/AccesslinkStore';
import AccesslinkCalls from '@/api/modules/AccesslinkCalls.js';

import AccesslinkTopBar from "./components/AccesslinkTopBar.vue"
import AccesslinkSidemenu from "./components/sidemenu/AccesslinkPodSidemenu.vue"

import MimetypeHelper from '@/components/MimetypeHelper.js'
import PlyrPlayer from '../pods2/components/players/PlyrPlayer.vue'
import AudioPlayer from '../pods2/components/players/AudioPlayer.vue'
import PodImageView from '../pods2/components/PodImageView.vue'
import NoPreviewAvailable from '../pods2/components/NoPreviewAvailable.vue'
import QuickPodSwitcher from '@/layouts2/mainLayout/components/topBars/QuickPodSwitcher.vue';
import { formatSize } from '@/components/formatters.js';
import { isMobileOrSlowConnection } from '@/components/utils/mobile-detection.js';

export default {
  name: 'AccesslinkPodView',
  components: {
    QuickPodSwitcher,
    AccesslinkTopBar,
    AccesslinkSidemenu,

    PlyrPlayer,
    AudioPlayer,
    PodImageView,
    NoPreviewAvailable,
  },
  data() {
    return {
      code: "",
      sidemenuDrawer: null,
      openedTreeNode: null,
      trackedPodId: null,
    };
  },
  beforeMount() {
    this.maybeHideQuickSwitcher();

    this.code = this.$route.params.code;

    if (this.getAccesslinkPods.length === 0 && this.getAccesslinkNodes.length === 0) {
      this.$router.replace({ name: 'accesslink:index' });
      return;
    }

    const theme = useTheme();

    if (
      this.getAccesslinkInfo &&
      this.getAccesslinkInfo.brand &&
      this.getAccesslinkInfo.brand.theme &&
      this.getAccesslinkInfo.brand.theme === 'dark'
    ) {
      // theme.global.name.value = 'dark';
      theme.change('dark')
    } else {
      // theme.global.name.value = 'light';
      theme.change('light')
    }

    const accesslinkStore = useAccesslinkStore();
    accesslinkStore.setViewPod(
      accesslinkStore.accesslinkPods.find((pod) => pod.id === this.$route.params.podid)
      || accesslinkStore.accesslinkNodes.find((node) => node.id === this.$route.params.nodeid)?.pods.find((pod) => pod.id === this.$route.params.podid),
    );
  },
  beforeUnmount() {
    const accesslinkStore = useAccesslinkStore();
    accesslinkStore.setViewPod(null);
  },
  mounted() {
    this.trackPodView();

    if (this.isArchivePod) {
      const accesslinkStore = useAccesslinkStore();
      accesslinkStore.refreshArchivePodContents();
    }
  },
  watch: {
    '$route.params.podid': function() {
      this.trackPodView();
    },
  },
  methods: {
    formatSize,
    trackPodView: function() {
      const podId = this.$route.params.podid;
      const accessToken = this.getAccesslinkInfo?.accesstoken;

      if (!podId || !accessToken || !this.pod || this.trackedPodId === podId) {
        return;
      }

      this.trackedPodId = podId;
      AccesslinkCalls.trackPodView({
        podid: podId,
        accesstoken: accessToken,
      }).catch(() => null);
    },
    toggleSidemenu() {
      this.sidemenuDrawer = !this.sidemenuDrawer;
    },
    downloadAsset: async function() {
      const rawAsset = this.pod?.asset?.raw?.[0];
      const accessToken = this.getAccesslinkInfo?.accesstoken;

      if (!rawAsset?.id || !accessToken) {
        return;
      }

      const { url } = await AccesslinkCalls.getAccesslinkAssetDownloadUrl({
        assetid: rawAsset.id,
        accesstoken: accessToken,
      });

      if (url) {
        window.location.href = url;
      }
    },
    onCommentTimeFocus(time) {
      if (!this.isPlayerPlaying) {
        const playerStore = usePlayerStore();
        playerStore.setSeekTimeEvent(time);
      }
    },
    maybeHideQuickSwitcher() {
      if (this.isQuickSwitcherVisible) {
        const accesslinkStore = useAccesslinkStore();
        accesslinkStore.toggleQuickSwitcher();
      }
    },
    switchToPod(podId) {
      this.maybeHideQuickSwitcher();

      if (this.$route.params.nodeid) {
        this.$router.push({
          name: 'accesslink:view:pod',
          params: { podid: podId, nodeid: this.$route.params.nodeid }
        });
      } else {
        this.$router.push({
          name: 'accesslink:view:pod',
          params: { podid: podId }
        });
      }
    }
  },
  computed: {
    pod: function() {
      const podid = this.$route.params.podid;
      const nodeid = this.$route.params.nodeid;

      if (nodeid) {
        const node = this.getAccesslinkNodes.find((node) => node.id === nodeid);
        console.assert(`Could not find node with id ${nodeid}`);
        return node.pods.find((pod) => pod.id === podid);
      }

      for (const pod of this.getAccesslinkPods) {
        if (pod.id === podid) {
          return pod;
        }

        if (this.showVersions && Array.isArray(pod.childrenpods) && pod.childrenpods.length > 0) {
          for (const childpod of pod.childrenpods) {
            if (childpod.id === podid) {
              return childpod;
            }
          }
        }
      }

      return null;
    },
    parentPod: function() {
      const podid = this.$route.params.podid;
      if (!podid) {
        return null;
      }

      for (const parentPod of this.getAccesslinkPods) {
        if (parentPod.id === podid) {
          return parentPod;
        }

        if (this.showVersions && Array.isArray(parentPod.childrenpods) && parentPod.childrenpods.length > 0) {
          for (const childpod of parentPod.childrenpods) {
            if (childpod.id === podid) {
              return parentPod;
            }
          }
        }
      }

      return null;
    },

    isVideoPod: function() {
      return MimetypeHelper.isMimetypeVideo(this.pod);
    },
    isAudioPod: function() {
      return MimetypeHelper.isMimetypeAudio(this.pod);
    },
    isImagePod: function() {
      return MimetypeHelper.isMimetypeImage(this.pod);
    },
    isArchivePod: function () {
      return MimetypeHelper.isMimetypeArchive(this.pod);
    },
    getAccesslinkInfo: function() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkInfo;
    },
    getAccesslinkPods: function() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkPods;
    },
    getAccesslinkNodes: function() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getAccesslinkNodes;
    },
    getNotes: function() {
      if (this.getAccesslinkInfo && this.getAccesslinkInfo.note) {
        return marked(this.getAccesslinkInfo.note, { breaks: false });
      }

      return "";
    },

    hasTranscodedFile() {
      if (this.pod &&
        this.pod.asset &&
        this.pod.asset.web &&
        this.pod.asset.web.length > 0
      ) {
        return true;
      }

      return false;
    },
    hasThumbnailFile() {
      if (this.pod &&
        this.pod.asset &&
        this.pod.asset.thumbnail &&
        this.pod.asset.thumbnail.length > 0
      ) {
        return true;
      }

      return false;
    },

    downloadingAllowed: function() {
      if (this.getAccesslinkInfo && this.getAccesslinkInfo.params) {
        return this.getAccesslinkInfo.params.allowdownloading;
      }
      return false;
    },
    allowSourceSelection: function() {
      if (this.getAccesslinkInfo && this.getAccesslinkInfo.params) {
        if (isMobileOrSlowConnection()) {
          return false;
        }
        return Boolean(this.getAccesslinkInfo.params.allowplayingraw);
      }
      return false;
    },
    getComments: function() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.getComments;
    },
    getCommentsWithStart: function() {
      return this.getComments.filter(x => x.start !== null);
    },
    isPlayerPlaying: function () {
      const playerStore = usePlayerStore();
      return playerStore.isPlaying;
    },
    isQuickSwitcherVisible() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.isQuickSwitcherVisible;
    },
    quickSwitcherPods() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.quickSwitcherPods(this.$route.params.nodeid);
    },
    showVersions() {
      return this.getAccesslinkInfo?.params?.addversions === 1;
    },
    isRefreshingArchiveContents() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.isRefreshingArchivePodContents;
    },
    archiveContents() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.archivePodContents;
    },
    listArchiveContentsError() {
      const accesslinkStore = useAccesslinkStore();
      return accesslinkStore.listArchivePodContentsError;
    }
  }
}
</script>

<style lang="scss" scoped>
#accesslink-main-container {
  min-height: 100%;
  padding: 16px 12px;
}

.accesslink-notes {
  padding: 20px;
  border-radius: 10px;

  :global(.v-theme--light &) {
    background-color: rgb(var(--v-theme-tw-surface-accent));
    /* background-color: #FFFFFF; */
  }

  :global(.v-theme--dark &) {
    background-color: rgb(var(--v-theme-tw-surface-accent));
    /* background-color: #232b36; */
  }
}
</style>
